import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { isAuthenticated, getCurrentUser, logout } from '../../services/authService';
import { isAuthenticated as checkAuth } from '../../services/authService';
import { getInitials } from '../../utils/formatters';
import { format } from 'date-fns';
import apiService, { getActiveSessionsCount } from '../../services/apiService';
import ActiveSessionsDialog from './ActiveSessionsDialog';
import {
  AppBar,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Button,
  Box,
  Chip
} from '@mui/material';

/**
 * Header component for the application using Saffron UI components
 * @returns {JSX.Element} Header component
 */
const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = getCurrentUser();
  const [anchorEl, setAnchorEl] = useState(null);
  const [lastImportInfo, setLastImportInfo] = useState({
    lastImport: null,
    lastImportType: null,
    lastImportStatus: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSessionsCount, setActiveSessionsCount] = useState(0);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [openSessionsDialog, setOpenSessionsDialog] = useState(false);
  const [isEventBasedProcessing, setIsEventBasedProcessing] = useState(false);

  // Fetch processing config to know if we show "Event based - RealTime" or last import time
  useEffect(() => {
    if (!isAuthenticated()) return;
    const fetchProcessingConfig = async () => {
      try {
        const response = await apiService.get('/api/processing-config');
        if (response?.success && response?.data) {
          const eventBased = response.data.webhookEnabled && !response.data.batchEnabled;
          setIsEventBasedProcessing(!!eventBased);
        }
      } catch (err) {
        console.error('Error fetching processing config:', err);
      }
    };
    fetchProcessingConfig();
  }, []);

  useEffect(() => {
    // Only fetch import times if user is authenticated and not event-based (event-based = real-time, no "last import" stamp)
    if (!isAuthenticated() || isEventBasedProcessing) {
      if (isEventBasedProcessing) setLoading(false);
      return;
    }

    const fetchLastImportTimes = async () => {
      try {
        setLoading(true);
        const response = await apiService.get('/api/import/last-import-times');
        if (response.success) {
          setLastImportInfo({
            lastImport: response.data.lastImport,
            lastImportType: response.data.lastImportType,
            lastImportStatus: response.data.lastImportStatus
          });
        } else {
          setError('Failed to fetch last import times');
        }
      } catch (err) {
        console.error('Error fetching last import times:', err);
        setError('Failed to fetch last import times');
      } finally {
        setLoading(false);
      }
    };

    fetchLastImportTimes();

    // Refresh the data every hour
    const intervalId = setInterval(fetchLastImportTimes, 60 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [isEventBasedProcessing]);

  useEffect(() => {
    const fetchActivityStats = async () => {
      try {
        setLoadingActivity(true);
        const response = await getActiveSessionsCount();
        console.log('Active sessions count response:', response);
        if (response && response.success) {
          const count = response.count || 0;
          console.log('Setting active sessions count:', count);
          setActiveSessionsCount(count);
        } else {
          console.log('No count in response or response not successful');
          setActiveSessionsCount(0);
        }
      } catch (err) {
        console.error('Error fetching active sessions count:', err);
        // Silently fail - don't show error in header
        setActiveSessionsCount(0);
      } finally {
        setLoadingActivity(false);
      }
    };

    fetchActivityStats();

    // Refresh activity stats every 30 seconds
    const activityIntervalId = setInterval(fetchActivityStats, 30 * 1000);

    return () => clearInterval(activityIntervalId);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch (err) {
      console.error('Error formatting date:', err);
      return 'Invalid date';
    }
  };
  
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate('/login');
  };
  
  return (
    <AppBar 
      position="static" 
      color="primary"
      sx={{
        boxShadow: 3,
        backdropFilter: 'blur(10px)',
      }}
    >
      <Toolbar sx={{ minHeight: '64px !important' }}>
        <Typography 
          variant="h6" 
          sx={{ 
            flexGrow: 1,
            fontWeight: 600,
            letterSpacing: '-0.01em',
          }}
        >
          HighQ Metrics
        </Typography>
        
        {/* Active Sessions Chip - Always visible and clickable */}
        <Box sx={{ mr: 2 }}>
          <Chip
            label={loadingActivity ? 'Loading...' : `${activeSessionsCount} Active Session${activeSessionsCount !== 1 ? 's' : ''}`}
            size="small"
            onClick={() => setOpenSessionsDialog(true)}
            sx={{
              bgcolor: activeSessionsCount > 0 ? '#ff9800' : '#4caf50',
              color: '#FFFFFF !important',
              fontSize: '0.8125rem',
              height: '32px',
              minWidth: '120px',
              fontWeight: 600,
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              cursor: 'pointer',
              borderRadius: 2,
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                opacity: 0.9,
                transform: 'translateY(-2px) scale(1.05)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              },
              '&:active': {
                transform: 'translateY(0) scale(1)',
              },
              '& .MuiChip-label': {
                px: 1.5,
                color: '#FFFFFF !important',
                fontWeight: 600,
                fontSize: '0.8125rem',
                textTransform: 'none'
              }
            }}
          />
        </Box>
        
        {/* Active Sessions Dialog */}
        <ActiveSessionsDialog
          open={openSessionsDialog}
          onClose={() => setOpenSessionsDialog(false)}
        />
        
        {isAuthenticated() && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'flex-end',
            mr: 3
          }}>
            {isEventBasedProcessing ? (
              <Typography variant="body2" sx={{ color: '#FFFFFF', fontSize: '0.875rem', fontWeight: 500 }}>
                Event based - RealTime
              </Typography>
            ) : loading ? (
              <Typography variant="body2" sx={{ color: '#FFFFFF', fontSize: '0.875rem' }}>
                Loading import information...
              </Typography>
            ) : error ? (
              <Typography variant="body2" sx={{ color: '#ffebee', fontSize: '0.875rem' }}>
                {error}
              </Typography>
            ) : lastImportInfo.lastImport ? (
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                  <Typography variant="body2" sx={{ color: '#FFFFFF', fontSize: '0.875rem', fontWeight: 500 }}>
                    Last {lastImportInfo.lastImportType === 'delta' ? 'Delta' : 'Full'} Import:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#FFFFFF', fontSize: '0.875rem' }}>
                    {formatDate(lastImportInfo.lastImport)}
                  </Typography>
                  {lastImportInfo.lastImportStatus && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.875rem',
                        color: lastImportInfo.lastImportStatus === 'success' ? '#81c784' : 
                               lastImportInfo.lastImportStatus === 'failed' ? '#e57373' : '#ffb74d',
                        fontStyle: 'italic',
                        fontWeight: 500
                      }}
                    >
                      ({lastImportInfo.lastImportStatus})
                    </Typography>
                  )}
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: '#FFFFFF', fontSize: '0.875rem' }}>
                No imports yet
              </Typography>
            )}
          </Box>
        )}
        
        {isAuthenticated() && user && user.role === 'admin' && (
          <Box sx={{ mr: 2 }}>
            <Button
              component={Link}
              to="/admin"
              color={location.pathname.startsWith('/admin') ? 'secondary' : 'inherit'}
              variant="text"
              sx={{
                borderRadius: 2,
                px: 2,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Admin
            </Button>
          </Box>
        )}
        
        {isAuthenticated() && (
          <Box>
            <Avatar 
              onClick={handleMenuOpen}
              sx={{ 
                cursor: 'pointer',
                bgcolor: 'secondary.main',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'scale(1.1)',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                },
              }}
            >
              {user && getInitials(user.username)}
            </Avatar>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem disabled>{user && user.username}</MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
